import { zodResolver } from '@hookform/resolvers/zod';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  Divider,
  Link,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useLocation, useNavigate } from 'react-router-dom';
import { login } from '../api/auth';
import { checkCampaignActive, checkReferrer } from '../api/campaign';
import LabeledTextField from '../components/LabeledTextField';
import { loginSchema } from '../schemas/loginSchema';
import { setCampaignCode, setCompanyId } from '../utils/storage';

const LoginPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isCheckingReferrer, setIsCheckingReferrer] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    setError: setFormError,
  } = useForm({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      email: '',
      password: '',
    },
  });

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const companyIdParam = params.get('companyId');
    const priceIdParam = params.get('price_id');

    if (companyIdParam) {
      setCompanyId(companyIdParam);
    }

    if (priceIdParam) {
      setCampaignCode(priceIdParam);

      // リファラチェック処理
      const checkReferrerAndActive = async () => {
        setIsCheckingReferrer(true);

        try {
          // document.referrerが存在しない場合もリファラチェックを実行（空文字列を送信）
          const referrer = document.referrer || '';
          try {
            await checkReferrer(priceIdParam, referrer);
          } catch (error) {
            // リファラ不一致の場合はエラー画面へ遷移
            setIsCheckingReferrer(false);
            navigate('/error?errorType=referrer');
            return;
          }

          // リファラチェック成功後、既存のcheckCampaignActive処理を実行
          const res = await checkCampaignActive(priceIdParam);
          if (res.is_active === 0) {
            setIsCheckingReferrer(false);
            navigate('/error?errorType=campaign_inactive');
            return;
          }

          setIsCheckingReferrer(false);
        } catch (error) {
          setIsCheckingReferrer(false);
          navigate('/error?errorType=campaign_inactive');
        }
      };

      checkReferrerAndActive();
    }
  }, [location.search, navigate]);

  const onSubmit = async (data) => {
    try {
      const res = await login(data.email, data.password);

      if (res.success) {
        const { token, role } = res;
        localStorage.setItem('token', token);

        if (role === 'admin') {
          navigate('/adminpage');
        } else if (role === 'user') {
          navigate('/mypage');
        } else {
          navigate('/implpage');
        }
      } else {
        setFormError('root', {
          type: 'manual',
          message: 'ログインに失敗しました',
        });
      }
    } catch {
      setFormError('root', {
        type: 'manual',
        message: '通信エラー',
      });
    }
  };

  // ローディング中はログイン画面を非表示にし、CircularProgressを表示
  if (isCheckingReferrer) {
    return (
      <Container
        component="main"
        maxWidth="xs"
        sx={{
          py: { xs: '7vh', sm: '10vh', md: '10vh', lg: '10vh' },
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
        }}
      >
        <CircularProgress />
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
          認証情報を確認中...
        </Typography>
      </Container>
    );
  }

  return (
    <Container
      component="main"
      maxWidth="xs"
      sx={{
        py: { xs: '7vh', sm: '10vh', md: '10vh', lg: '10vh' },
      }}
    >
      <Paper
        elevation={3}
        sx={{
          padding: 3,
          marginBottom: '2vh',
        }}
      >
        <Typography
          variant="h3"
          align="center"
          sx={{
            margin: 2,
          }}
        >
          ログイン
        </Typography>

        <Divider
          sx={{
            width: '100%',
            borderColor: '#32c86b',
            borderBottomWidth: 3,
          }}
        />

        <Box
          component="form"
          onSubmit={handleSubmit(onSubmit)}
          sx={{
            maxWidth: 'none',
            boxShadow: 'none',
            minHeight: 'fit-content',
            padding: 0,
            mt: '5dvh',
            alignItems: 'flex-start',
          }}
        >
          <LabeledTextField
            label="メールアドレス"
            id="email"
            name="email"
            type="email"
            autoComplete="email"
            autoFocus
            placeholder="xxx@example.com"
            register={register}
            error={errors.email}
          />

          <LabeledTextField
            label="パスワード"
            id="password"
            name="password"
            type="password"
            autoComplete="current-password"
            placeholder="パスワードを入力してください"
            register={register}
            error={errors.password}
          />

          {errors.root && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {errors.root.message}
            </Alert>
          )}

          <Button
            type="submit"
            fullWidth
            variant="contained"
            color="primary"
            margin="none"
            sx={{ mt: 3, mb: 2, borderRadius: 5 }}
            disabled={isSubmitting}
          >
            {isSubmitting ? 'ログイン中...' : 'ログイン'}
          </Button>

          <Box sx={{ marginTop: 2 }}>
            <Stack direction="column" spacing={1} alignItems="flex-start">
              <Link
                component="button"
                type="button"
                variant="body2"
                color="info"
                onClick={() => navigate('/forgot-id')}
                sx={{
                  '&:hover': {
                    backgroundColor: 'transparent',
                    color: 'info.dark'
                  },
                }}
              >
                メールアドレスを忘れた方はこちら
                
              </Link>

              <Link
                component="button"
                type="button"
                variant="body2"
                color="info"
                onClick={() => navigate('/forgot-password')}
                sx={{
                  '&:hover': {
                    backgroundColor: 'transparent',
                    color: 'info.dark'
                  },
                }}
              >
                パスワードを忘れた方はこちら
              </Link>
            </Stack>
          </Box>

          <Divider
            sx={{
              borderColor: 'primary.main',
              width: '100%',
            }}
          />

          <Box sx={{ mt: 2, width: '100%' }}>
            <Typography variant="h6" gutterBottom>
              新規会員の登録
            </Typography>
            <Button
              variant="outlined"
              fullWidth
              color="primary"
              onClick={() => navigate('/signup')}
              sx={{ mt: 1, borderRadius: 5 }}
            >
              新規会員の登録
            </Button>
          </Box>
        </Box>
      </Paper>
    </Container>
  );
};

export default LoginPage;
